/* eslint-disable react/no-unescaped-entities */
/**
 * EthicsPage · /ethics
 * ---------------------------------------------------------------------------
 * Public charter that clearly states what ErrorHacker does and doesn't do.
 * Required for compliance with payment providers, app stores, and platform
 * marketplaces. Deep-linked from the footer + About page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Ban, GraduationCap, BookOpen, Users, Terminal, ArrowRight } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const WeDo = ({ icon: I, title, body, color }) => (
  <div className="rounded-2xl border-2 p-5 relative overflow-hidden" style={{ borderColor: `${color}55`, background: `linear-gradient(160deg, ${color}0c 0%, transparent 65%)` }}>
    <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${color}18`, border: `1px solid ${color}55`, color }}>
        <I size={18} strokeWidth={1.9} />
      </div>
      <div className="font-black text-[15px]" style={{ fontFamily: 'Inter,sans-serif' }}>{title}</div>
    </div>
    <p className="text-[13px] opacity-80 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{body}</p>
  </div>
);

const WeDont = ({ text }) => (
  <li className="flex gap-3 text-[13.5px] leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
    <Ban size={15} className="mt-0.5 shrink-0" style={{ color: '#ff5078' }} strokeWidth={2} />
    <span className="opacity-90">{text}</span>
  </li>
);

const EthicsPage = () => {
  const { config } = useSiteConfig();
  const site = config.site || {};
  const name = site.name || 'ErrorHacker';
  const email = site.email || 'ethics@errorhacker.site';
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 sm:py-24 overflow-hidden eh-grid-bg">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 40%, rgba(0,255,157,.09), transparent 60%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="eh-kicker justify-center mb-4">// PUBLIC CHARTER · ETHICS</div>
          <h1 className="font-black leading-tight" style={{ fontSize: 'clamp(2rem, 6vw, 4rem)', fontFamily: 'Cinzel, serif' }}>
            OUR <span className="eh-neon">ETHICAL LINE</span>.
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base opacity-80 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            {name} is a cyber-defense education studio. We teach, defend, and coach —
            we never attack, take unauthorized actions, or run grey-market ops.
            This page states exactly what we do and refuse to do, in plain English.
          </p>
          <div className="mt-3 eh-mono text-[11px] opacity-60 tracking-widest">Effective February 2026</div>
        </div>
      </section>

      {/* What we DO */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <div className="eh-kicker mb-2">// WHAT WE DO</div>
            <h2 className="font-black" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2rem)', fontFamily: 'Cinzel, serif' }}>Legitimate, defensive, educational.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <WeDo
              icon={GraduationCap} color="#00ff9d"
              title="Cybersecurity Education"
              body="Books, playbooks, and structured courses covering OSINT, defensive security, personal privacy, and ethical bug-bounty hunting."
            />
            <WeDo
              icon={ShieldCheck} color="#4de0ff"
              title="Cyber-Incident Consultation"
              body="Advisory sessions for victims of scams, hacks, or doxing. We coach you through the platform's official recovery process and post-incident hardening. We never access your accounts."
            />
            <WeDo
              icon={Terminal} color="#c084fc"
              title="Educational Recon Tools"
              body="Free WHOIS, IP intel, CVE search, subdomain enumeration and username OSINT — the same public-data lookups any security researcher uses to learn."
            />
            <WeDo
              icon={Users} color="#ff2d92"
              title="Ethical Bug-Bounty Coaching"
              body="Structured mentorship for aspiring bug-bounty hunters on HackerOne, Bugcrowd, and Intigriti. Curated writeups, methodology, disclosure discipline."
            />
            <WeDo
              icon={BookOpen} color="#ffd34d"
              title="Community & Memberships"
              body="Private community access, live AMAs with senior operators, and monthly deep-dive walkthroughs of publicly-disclosed CVEs and case studies."
            />
            <WeDo
              icon={ShieldCheck} color="#ff8a4d"
              title="Authorized Security Audits"
              body="B2B penetration testing and configuration reviews — only under a signed engagement scope from the asset owner. No exceptions, no verbal agreements."
            />
          </div>
        </div>
      </section>

      {/* What we DON'T */}
      <section className="py-16 border-t border-[var(--eh-border)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <div className="eh-kicker mb-2" style={{ color: '#ff5078' }}>// WHAT WE REFUSE</div>
            <h2 className="font-black" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2rem)', fontFamily: 'Cinzel, serif' }}>The clear no-line.</h2>
          </div>
          <div className="rounded-2xl border-2 p-6" style={{ borderColor: 'rgba(255,80,120,.35)', background: 'rgba(255,80,120,.05)' }}>
            <ul className="space-y-3">
              <WeDont text="Unauthorized account recovery — we do NOT log in to, take over, or manipulate accounts on your behalf. We only coach you through the platform's official channels." />
              <WeDont text="Fake social-media engagement — no fake followers, likes, views, comments, or bot networks. We do not sell traffic that violates platform Terms of Service." />
              <WeDont text="Unauthorized access to third-party systems — pentesting requires a signed scope. No agreement, no engagement. Ever." />
              <WeDont text="Doxing, stalkerware, revenge campaigns, or personal-vendetta ops of any kind." />
              <WeDont text="SIM-swap, synthetic-identity, credential-stuffing, or financial-fraud services." />
              <WeDont text="Selling exploits, zero-days, or offensive tooling to unvetted buyers." />
              <WeDont text="Working with clients whose stated goals violate applicable law in their jurisdiction or in ours." />
            </ul>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="eh-panel p-6 sm:p-8" style={{ borderColor: 'rgba(0,255,157,.35)' }}>
            <div className="eh-kicker mb-2">// COMPLIANCE</div>
            <h3 className="font-black mb-3" style={{ fontSize: 'clamp(1.2rem, 3vw, 1.7rem)', fontFamily: 'Cinzel, serif' }}>How we enforce this</h3>
            <ul className="space-y-2 text-[13.5px] opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
              <li className="flex gap-2"><span className="text-[var(--eh-green)]">✓</span> Every consultation intake includes a signed statement of consent + scope.</li>
              <li className="flex gap-2"><span className="text-[var(--eh-green)]">✓</span> All B2B pentests require a written Statement of Work signed by an authorized signatory of the asset owner.</li>
              <li className="flex gap-2"><span className="text-[var(--eh-green)]">✓</span> Chat channels are monitored by senior operators — misuse is refunded and reported.</li>
              <li className="flex gap-2"><span className="text-[var(--eh-green)]">✓</span> All payments are processed through vetted, licensed providers. No cash-in-hand ops.</li>
              <li className="flex gap-2"><span className="text-[var(--eh-green)]">✓</span> We publish an updated version of this charter every quarter.</li>
            </ul>
            <p className="mt-5 text-[13px] opacity-75 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
              Spotted a service or piece of content on {name} that seems to cross this line?
              Email <a href={`mailto:${email}`} className="eh-neon underline decoration-dotted">{email}</a> — every report is reviewed within 24 hours and, if valid, the offending page is taken down while we investigate.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="font-black mb-3" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)', fontFamily: 'Cinzel, serif' }}>
            Want to <span className="eh-neon">work with us</span>?
          </h3>
          <p className="opacity-75 max-w-lg mx-auto text-sm mb-6" style={{ fontFamily: 'Inter,sans-serif' }}>
            Start with the free recon tools or browse our education library. If you need advisory work, request a consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/tools" className="eh-btn-primary text-xs">
              FREE TOOLS <ArrowRight size={12} />
            </Link>
            <Link to="/books" className="eh-btn-ghost text-xs">EDUCATION LIBRARY</Link>
            <Link to="/advisory" className="eh-btn-ghost text-xs">REQUEST CONSULTATION</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EthicsPage;
